import React, { FC, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { Button, ConditionalWrapper, InfoIcon, Tooltip } from 'src/common';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import KeywordsQuickFilter from './KeywordsQuickFilter';
import BuyerQuickFilter from './BuyerQuickFilter';
import { FeatureFlag, ProfileType } from '@tendium/prom-types';
import ContractValueQuickFilter from './ContractValueQuickFilter';
import SaveAsNew from './SaveAsNew';
import { useLoadMp, useUpdateMp } from 'src/models/matchingProfiles/hooks';
import SupplierQuickFilter from './SupplierQuickFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import SavedSearchQuickActions from './SavedSearchQuickActions';
import { TendersTypesVars } from 'src/models/procurements/Tenders/types';
import isEqual from 'lodash/isEqual';
import { FilterClear, FilterCodes, FilterContractEnd, FilterSearch } from 'src/shared';
import FilterProfiles from './FilterProfiles';
import { toSupplierIds, toSupplierStrings } from 'src/models/procurements/Tenders/helpers';
import { isNumber, useFeatureFlag } from 'src/helpers';

export const SearchBar: FC = () => {
  const { t } = useTranslation();

  const [{ vars, initVars, eventSource: type }] = useApiTenders();

  const profileId = vars.profileId;
  const { data: mp, loading } = useLoadMp(profileId);

  const isExpiringContractsPageAvailable = useFeatureFlag(FeatureFlag.AnalyticsExpiringContracts);

  const clearableFieldKeys: TendersTypesVars[typeof type] = useMemo(
    () => [
      'cpvCodesExact',
      'cpvCodes',
      'nutsCodesExact',
      'nutsCodes',
      'buyerOrgIds',
      'buyerSearchStrings',
      'procuringAgencyTypes',
      'supplierOrgIds',
      'supplierSearchStrings',
      'supplierSearchLogic',
      'contractValueRange',
      'keywordArgs',
      'contractValueIncludeMissingValue',
      'search',
      'profileId',
      'contractEnd',
      'contractEndSearchMode'
    ],
    []
  );
  const [saveAsNewVisible, setSaveAsNewVisible] = useState(false);
  const [updateMp] = useUpdateMp();
  const isKeywordsMpDiff = useMemo(() => {
    const mpKeywords =
      mp?.keywords?.map(keyword => ({
        value: keyword.value,
        searchType: keyword.searchType,
        searchLogic: keyword.searchLogic,
        filterLogic: keyword.filterLogic,
        highlightColor: keyword.highlightColor
      })) ?? [];
    const varsKeywords =
      vars.keywordArgs?.map(keyword => ({
        value: keyword.value,
        searchType: keyword.searchType,
        searchLogic: keyword.searchLogic,
        filterLogic: keyword.filterLogic,
        highlightColor: keyword.highlightColor
      })) ?? [];
    return mp?.profileType === ProfileType.ExpiringContract && !isEqual(mpKeywords, varsKeywords);
  }, [mp?.keywords, mp?.profileType, vars.keywordArgs]);
  const isKeywordsDot = useMemo(
    () => isKeywordsMpDiff || (mp?.profileType !== ProfileType.ExpiringContract && !!vars.keywordArgs?.length),
    [isKeywordsMpDiff, mp?.profileType, vars.keywordArgs?.length]
  );
  const isSuppliersMpDiff = useMemo(
    () =>
      mp?.profileType === ProfileType.ExpiringContract &&
      (!isEqual(vars.supplierOrgIds ?? [], toSupplierIds(mp.supplierSearchTerms) ?? []) ||
        !isEqual(vars.supplierSearchStrings ?? [], toSupplierStrings(mp.supplierSearchTerms) ?? []) ||
        vars.supplierSearchLogic !== mp.supplierSearchLogic),
    [mp, vars.supplierOrgIds, vars.supplierSearchLogic, vars.supplierSearchStrings]
  );
  const isSuppliersDot = useMemo(
    () =>
      isSuppliersMpDiff ||
      (mp?.profileType !== ProfileType.ExpiringContract &&
        (!!vars.supplierOrgIds?.length ||
          !!vars.supplierSearchStrings?.length ||
          vars.supplierSearchLogic !== initVars.supplierSearchLogic)),
    [
      initVars.supplierSearchLogic,
      isSuppliersMpDiff,
      mp?.profileType,
      vars.supplierOrgIds?.length,
      vars.supplierSearchLogic,
      vars.supplierSearchStrings?.length
    ]
  );
  const isBuyersMpDiff = useMemo(
    () =>
      mp?.profileType === ProfileType.ExpiringContract &&
      (!isEqual(vars.buyerOrgIds ?? [], mp?.buyerOrgIds ?? []) ||
        !isEqual(vars.buyerSearchStrings ?? [], mp?.buyerSearchStrings ?? []) ||
        !isEqual(vars.procuringAgencyTypes ?? [], mp?.buyerTypes ?? [])),
    [mp, vars.buyerOrgIds, vars.buyerSearchStrings, vars.procuringAgencyTypes]
  );
  const isBuyersDot = useMemo(
    () =>
      isBuyersMpDiff ||
      (mp?.profileType !== ProfileType.ExpiringContract &&
        (!!vars.buyerOrgIds?.length || !!vars.buyerSearchStrings?.length || !!vars.procuringAgencyTypes?.length)),
    [
      isBuyersMpDiff,
      mp?.profileType,
      vars.buyerOrgIds?.length,
      vars.buyerSearchStrings?.length,
      vars.procuringAgencyTypes?.length
    ]
  );
  console.debug({ mp, vars });
  const isContractValuesMpDiff = useMemo(() => {
    const mpMin = mp?.contractValueMin ?? null;
    const mpMax = mp?.contractValueMax ?? null;
    const mpCurrency = mp?.contractValueCurrency ?? null;
    const varsMin = !!vars.contractValueRange?.min ? vars.contractValueRange?.min : null;
    const varsMax = !!vars.contractValueRange?.max ? vars.contractValueRange?.max : null;
    const varsCurrency = !!vars.contractValueRange?.currency ? vars.contractValueRange?.currency : null;

    return (
      mp?.profileType === ProfileType.ExpiringContract &&
      (varsMin !== mpMin ||
        varsMax !== mpMax ||
        varsCurrency !== mpCurrency ||
        vars.contractValueIncludeMissingValue !== mp?.contractValueIncludeMissingValue)
    );
  }, [
    mp?.contractValueCurrency,
    mp?.contractValueIncludeMissingValue,
    mp?.contractValueMax,
    mp?.contractValueMin,
    mp?.profileType,
    vars.contractValueIncludeMissingValue,
    vars.contractValueRange?.currency,
    vars.contractValueRange?.max,
    vars.contractValueRange?.min
  ]);
  const isContractValuesDot = useMemo(
    () =>
      isContractValuesMpDiff ||
      (mp?.profileType !== ProfileType.ExpiringContract &&
        (!!vars.contractValueRange?.min ||
          !!vars.contractValueRange?.max ||
          !!vars.contractValueRange?.currency ||
          !!vars.contractValueIncludeMissingValue)),
    [
      isContractValuesMpDiff,
      mp?.profileType,
      vars.contractValueIncludeMissingValue,
      vars.contractValueRange?.currency,
      vars.contractValueRange?.max,
      vars.contractValueRange?.min
    ]
  );
  const isCpvsMpDiff = useMemo(
    () =>
      mp?.profileType === ProfileType.ExpiringContract &&
      (!isEqual(vars.cpvCodes ?? [], mp?.cpvCodes ?? []) ||
        !isEqual(vars.cpvCodesExact ?? [], mp?.cpvCodesExact ?? [])),
    [mp, vars.cpvCodes, vars.cpvCodesExact]
  );
  const isCpvDot = useMemo(
    () =>
      (mp?.profileType !== ProfileType.ExpiringContract && (!!vars.cpvCodes?.length || !!vars.cpvCodesExact?.length)) ||
      isCpvsMpDiff,
    [isCpvsMpDiff, mp?.profileType, vars.cpvCodes?.length, vars.cpvCodesExact?.length]
  );
  const isNutsMpDiff = useMemo(
    () =>
      mp?.profileType === ProfileType.ExpiringContract &&
      (!isEqual(vars.nutsCodes ?? [], mp?.nutsCodes ?? []) ||
        !isEqual(vars.nutsCodesExact ?? [], mp?.nutsCodesExact ?? [])),
    [mp, vars.nutsCodes, vars.nutsCodesExact]
  );
  const isNutsDot = useMemo(
    () =>
      (mp?.profileType !== ProfileType.ExpiringContract &&
        (!!vars.nutsCodes?.length || !!vars.nutsCodesExact?.length)) ||
      isNutsMpDiff,
    [isNutsMpDiff, mp?.profileType, vars.nutsCodes?.length, vars.nutsCodesExact?.length]
  );
  const isSaveEnabled = useMemo(
    () =>
      mp?.profileType === ProfileType.ExpiringContract &&
      (isKeywordsMpDiff ||
        isSuppliersMpDiff ||
        isBuyersMpDiff ||
        isContractValuesMpDiff ||
        isCpvsMpDiff ||
        isNutsMpDiff),
    [
      isBuyersMpDiff,
      isContractValuesMpDiff,
      isCpvsMpDiff,
      isKeywordsMpDiff,
      isNutsMpDiff,
      isSuppliersMpDiff,
      mp?.profileType
    ]
  );
  const onOpenSaveAsNew = useCallback(() => {
    setSaveAsNewVisible(true);
  }, []);
  const onCloseSaveAsNew = useCallback(() => {
    setSaveAsNewVisible(false);
  }, []);

  const onSaveExpProfile = useCallback(() => {
    profileId &&
      mp?.profileType === ProfileType.ExpiringContract &&
      updateMp({
        id: profileId,
        cpvCodesExact: vars.cpvCodesExact,
        cpvCodes: vars.cpvCodes,
        nutsCodesExact: vars.nutsCodesExact,
        nutsCodes: vars.nutsCodes,
        buyerOrgIds: vars.buyerOrgIds,
        buyerSearchStrings: vars.buyerSearchStrings,
        buyerTypes: vars.procuringAgencyTypes,
        supplierOrgIds: vars.supplierOrgIds,
        supplierSearchStrings: vars.supplierSearchStrings,
        supplierSearchLogic: vars.supplierSearchLogic,
        contractValueRange: {
          currency: vars.contractValueRange?.currency ?? undefined,
          max: isNumber(vars.contractValueRange?.max) ? Number(vars.contractValueRange?.max) : undefined,
          min: isNumber(vars.contractValueRange?.min) ? Number(vars.contractValueRange?.min) : undefined
        },
        contractValueIncludeMissingValue: vars.contractValueIncludeMissingValue,
        keywords: vars.keywordArgs?.map(keyword => ({
          value: keyword.value,
          searchType: keyword.searchType,
          searchLogic: keyword.searchLogic,
          filterLogic: keyword.filterLogic,
          highlightColor: keyword.highlightColor
        }))
      });
  }, [
    mp?.profileType,
    profileId,
    updateMp,
    vars.buyerOrgIds,
    vars.buyerSearchStrings,
    vars.contractValueIncludeMissingValue,
    vars.contractValueRange,
    vars.cpvCodes,
    vars.cpvCodesExact,
    vars.keywordArgs,
    vars.nutsCodes,
    vars.nutsCodesExact,
    vars.procuringAgencyTypes,
    vars.supplierOrgIds,
    vars.supplierSearchLogic,
    vars.supplierSearchStrings
  ]);
  return (
    <div className={styles.filters}>
      <div className={styles.topRowContainer}>
        <FilterSearch />
        <FilterContractEnd />
        <FilterProfiles />
        {mp?.profileType === ProfileType.Matching && <InfoIcon desc={t('ExpiringContracts.monitoringProfileInfo')} />}
        {mp?.profileType === ProfileType.ExpiringContract && (
          <div className={styles.quickActions}>
            <SavedSearchQuickActions />
          </div>
        )}
      </div>
      {!!isExpiringContractsPageAvailable && (
        <div className={styles.filterContainer}>
          <KeywordsQuickFilter loading={loading} special={isKeywordsDot} />
          <SupplierQuickFilter loading={loading} special={isSuppliersDot} />
          <BuyerQuickFilter loading={loading} special={isBuyersDot} />
          <ContractValueQuickFilter loading={loading} special={isContractValuesDot} />
          <FilterCodes type={'cpvCodes'} loading={loading} special={isCpvDot} />
          <FilterCodes type={'nutsCodes'} loading={loading} special={isNutsDot} />
          <ConditionalWrapper
            condition={mp?.profileType === ProfileType.Matching && !!vars.profileId}
            wrapper={children => <Tooltip title={t('ExpiringContracts.monitoringProfileInfoSave')}>{children}</Tooltip>}
          >
            <Button type={'link'} onClick={onSaveExpProfile} disabled={!isSaveEnabled}>
              {t('Common.save')}
            </Button>
          </ConditionalWrapper>
          <FilterClear filters={clearableFieldKeys} />
          <div className={styles.saveAsNew}>
            <Button type={'link'} onClick={onOpenSaveAsNew} icon={<FontAwesomeIcon icon={faPlus} />} disabled={loading}>
              {t('ExpiringContracts.saveAsNew')}
            </Button>
          </div>
        </div>
      )}
      {saveAsNewVisible && <SaveAsNew onHide={onCloseSaveAsNew} />}
    </div>
  );
};
export default SearchBar;
